import { getPhaseByCode, isSamePhase } from '../constants/object-workflow/workflow-model';
import {
  normalizeObjectTypeForLookup,
  PHASE_CODE,
} from '../constants/object-workflow/phase-codes';
import { isBudovaLastPhase } from '../constants/object-workflow/budova-milestone-engine';

/**
 * Materiál v object popupu — jen u fází / typů, kde dává smysl.
 *
 * Explicitně skryté (blacklist):
 * - typ `STAVAJICI` (informativní trasa — žádný materiál / balíčky záfuku)
 * - `VYKOP.1.PHASE.3` (Předaný)
 * - `BUDOVA.1.PHASE.1` (Zrušený)
 * - milník Odbočeno (`ODBOCENO`; Excel id `BUDOVA.2.PHASE.1.MILESTONE.ODBOCENY`)
 *
 * **Spolupráce** má přísnější gate: `isWorkShareRelevantPhase` (navíc skryté na
 * všech koncových fázích — `getPhaseByCode(...).isFinal`).
 *
 * **Keep in sync with KMP** `MaterialWorkSharePhaseUtil`.
 */
export const MATERIAL_WORK_SHARE_MILESTONE_BLACKLIST = ['ODBOCENO', 'ODBOCENY'] as const;

/** Phase names matched via `isSamePhase` (not raw WF codes). */
export const MATERIAL_WORK_SHARE_PHASE_NAME_BLACKLIST = [
  PHASE_CODE.VYKOP_PREDANY,
  PHASE_CODE.BUDOVA_ODMITNUTY,
] as const;

/**
 * Object types that never show the material affordance (picker / commit materialUsed).
 * Includes legacy aliases via `normalizeObjectTypeForLookup` (e.g. `VYKOP_STAVAJICI`).
 */
export const MATERIAL_OBJECT_TYPE_BLACKLIST = ['STAVAJICI'] as const;

export function isMaterialBlockedObjectType(
  objectType: string | null | undefined,
): boolean {
  const key = normalizeObjectTypeForLookup(String(objectType ?? ''));
  if (!key) return false;
  return (MATERIAL_OBJECT_TYPE_BLACKLIST as readonly string[]).includes(key);
}

export function isMaterialAndWorkShareRelevantPhase(
  status: string | null | undefined,
  selectedBudovaMilestoneId?: string | null,
  objectType?: string | null,
): boolean {
  if (isMaterialBlockedObjectType(objectType)) {
    return false;
  }

  const milestone = (selectedBudovaMilestoneId ?? '').trim().toUpperCase();
  if (
    (MATERIAL_WORK_SHARE_MILESTONE_BLACKLIST as readonly string[]).includes(milestone)
  ) {
    return false;
  }

  const current = (status ?? '').trim();
  if (!current) return true;

  for (const name of MATERIAL_WORK_SHARE_PHASE_NAME_BLACKLIST) {
    if (isSamePhase(current, name)) return false;
  }
  return true;
}

/**
 * Koncová fáze workflow (`isFinal` z generovaných workflow).
 * BUDOVA: Zafouknutý bez digitální cesty = terminální (parita `isBudovaLastPhase`).
 * Bez typu/statusu → false (neblokovat).
 */
export function isWorkflowStatusFinalPhase(
  objectType: string | null | undefined,
  status: string | null | undefined,
  opts?: { digitalniCesta?: number | null },
): boolean {
  const type = (objectType ?? '').trim();
  const code = (status ?? '').trim();
  if (!type || !code) return false;
  if (type === 'BUDOVA') {
    return isBudovaLastPhase(code, opts?.digitalniCesta);
  }
  return getPhaseByCode(type, code)?.isFinal === true;
}

/**
 * Spolupráce — jen pokud se propíše do výkazu: stejný materiálový blacklist
 * + skryté na všech koncových fázích (Předaný, Připojené, Svařený, Provařené, …).
 */
export function isWorkShareRelevantPhase(
  objectType: string | null | undefined,
  status: string | null | undefined,
  selectedBudovaMilestoneId?: string | null,
): boolean {
  if (
    !isMaterialAndWorkShareRelevantPhase(
      status,
      selectedBudovaMilestoneId,
      objectType,
    )
  ) {
    return false;
  }
  if (isWorkflowStatusFinalPhase(objectType, status)) {
    return false;
  }
  return true;
}

/** JSON shape for mobile hand-mirror / docs (`export-material-work-share-phase.js`). */
export function buildMaterialWorkSharePhaseExport(): {
  objectTypeBlacklist: readonly string[];
  milestoneBlacklist: readonly string[];
  phaseNameBlacklist: readonly string[];
  notes: string;
} {
  return {
    objectTypeBlacklist: MATERIAL_OBJECT_TYPE_BLACKLIST,
    milestoneBlacklist: MATERIAL_WORK_SHARE_MILESTONE_BLACKLIST,
    phaseNameBlacklist: MATERIAL_WORK_SHARE_PHASE_NAME_BLACKLIST,
    notes:
      'KMP MaterialWorkSharePhaseUtil: isRelevant = materiál (type+phase); isWorkShareRelevant = materiál + !isFinal. STAVAJICI never shows material. AttachmentPhase stays a separate code mirror.',
  };
}
